import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import type { ObjektUebersicht } from "@repo/shared";

import { Zahl } from "../components/kalkulation/Zahl";
import { api, errorMessage } from "../lib/api";
import { euroWert } from "../lib/objekt";

/**
 * Objekte: Kalkulationen über viele Positionen aus den Konfiguratoren –
 * Hotels, Wohnanlagen, Praxen. Jede Zeile führt in die Kalkulation.
 */
export function ObjektePage() {
  const navigate = useNavigate();
  const [objekte, setObjekte] = useState<ObjektUebersicht[]>([]);
  const [laedt, setLaedt] = useState(true);
  const [fehler, setFehler] = useState<string | null>(null);
  const [name, setName] = useState("");
  const [kunde, setKunde] = useState("");
  const [legtAn, setLegtAn] = useState(false);

  const laden = useCallback(async () => {
    try {
      const res = await api.api.admin.objekte.$get();
      if (!res.ok) throw new Error(await errorMessage(res, "Objekte nicht ladbar"));
      const daten = await res.json();
      if ("objekte" in daten) setObjekte(daten.objekte as ObjektUebersicht[]);
      setFehler(null);
    } catch (cause) {
      setFehler(cause instanceof Error ? cause.message : "Laden fehlgeschlagen");
    } finally {
      setLaedt(false);
    }
  }, []);

  useEffect(() => {
    void laden();
  }, [laden]);

  async function anlegen(e: React.FormEvent) {
    e.preventDefault();
    if (!name.trim()) return;
    setLegtAn(true);
    try {
      const res = await api.api.admin.objekte.$post({ json: { name: name.trim(), kunde: kunde.trim() || null } });
      if (!res.ok) throw new Error(await errorMessage(res, "Objekt nicht angelegt"));
      const objekt = (await res.json()) as { id: string };
      navigate(`/objekte/${objekt.id}`);
    } catch (cause) {
      setFehler(cause instanceof Error ? cause.message : "Anlegen fehlgeschlagen");
      setLegtAn(false);
    }
  }

  const gesamt = objekte.reduce((s, o) => s + o.vkNettoCents, 0);
  const gewinn = objekte.reduce((s, o) => s + o.gewinnNettoCents, 0);

  return (
    <section className="objekte">
      <div className="page-head">
        <h1>Objekte</h1>
        <form className="objekt-anlegen" onSubmit={(e) => void anlegen(e)}>
          <input type="text" placeholder="Neues Objekt, z. B. Hotel Seeblick" value={name} onChange={(e) => setName(e.target.value)} aria-label="Name des Objekts" />
          <input type="text" placeholder="Kunde (optional)" value={kunde} onChange={(e) => setKunde(e.target.value)} aria-label="Kunde" />
          <button type="submit" className="button kalk-knopf--primaer" disabled={legtAn || !name.trim()}>
            Anlegen
          </button>
        </form>
      </div>
      <p className="muted">
        Kalkulationen über viele Positionen aus den Konfiguratoren, je Position mit Einkauf, Verkauf und Montage, gruppiert nach Apartment und Zimmer.
      </p>

      {fehler && <p className="kalk-fehler" role="alert">{fehler}</p>}

      {objekte.length > 0 && (
        <div className="kalk-kennzahlen objekt-summen">
          <div className="kalk-kennzahl"><span>Objekte</span><strong><Zahl wert={objekte.length} /></strong></div>
          <div className="kalk-kennzahl"><span>VK netto gesamt</span><strong><Zahl wert={euroWert(gesamt)} art="euro" /></strong></div>
          <div className="kalk-kennzahl kalk-kennzahl--betont"><span>Gewinn gesamt</span><strong><Zahl wert={euroWert(gewinn)} art="euro" /></strong></div>
        </div>
      )}

      {laedt ? (
        <p className="muted">Lade Objekte …</p>
      ) : objekte.length === 0 ? (
        <div className="callout">
          <strong>Noch kein Objekt.</strong>
          <p>Oben ein Objekt anlegen, dann Positionen aus den Konfiguratoren hinzufügen.</p>
        </div>
      ) : (
        <table className="kalk-tabelle objekt-liste">
          <thead>
            <tr>
              <th>Objekt</th><th>Kunde</th><th className="num">Positionen</th><th className="num">VK netto</th><th className="num">Gewinn</th><th>Geändert</th>
            </tr>
          </thead>
          <tbody>
            {objekte.map((o) => (
              <tr key={o.id} className="objekt-zeile" onClick={() => navigate(`/objekte/${o.id}`)} tabIndex={0} onKeyDown={(e) => { if (e.key === "Enter") navigate(`/objekte/${o.id}`); }}>
                <td><strong>{o.name}</strong></td>
                <td>{o.kunde ?? <span className="muted">–</span>}</td>
                <td className="num">{o.anzahlPositionen}</td>
                <td className="num"><Zahl wert={euroWert(o.vkNettoCents)} art="euro" /></td>
                <td className={`num ${o.gewinnNettoCents >= 0 ? "kalk-gut" : "kalk-schlecht"}`}><Zahl wert={euroWert(o.gewinnNettoCents)} art="euro" /></td>
                <td className="muted small">{new Date(o.updatedAt).toLocaleDateString("de-DE")}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  );
}
